/** @jsxImportSource @emotion/react */
import * as yup from "yup";
import { css } from "@emotion/react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { toastr } from "react-redux-toastr";
import { useNavigate, useParams } from "react-router";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  AdyenBusinessLine,
  AdyenBusinessLineFormType,
} from "../../models/adyen-business-line";
import {
  useCreateAdyenAccountHolderBusinessLine,
  useGetBusinessLineIndustryCodes,
  useUpdateAdyenAccountHolderBusinessLine,
} from "../../service/adyen-account-holders-api";
import { getErrorMessage } from "../../../../error-handler";
import { FormRow } from "../../../common/hook-form/FormRow";
import { TextInput } from "../../../../components/forms/inputs/TextInput";
import { colors } from "../../../../components/colors";
import {
  ButtonAction,
  ButtonInternalLink,
  ButtonPrimary,
} from "../../../../components/NewButtons";
import { ActionSpinner } from "../../../../components/ActionSpinner";
import { IconButton } from "../../../../components/IconButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { StyledText } from "../../../../assets/typography/typography";
import { SpinnerBlock } from "../../../common/Spinner";
import { SelectInput } from "../../../../components/forms/inputs/SelectInput";
import { ErrorMessage } from "@hookform/error-message";

export const buttonWrapperStyle = css`
  position: sticky;
  bottom: 0;
  z-index: 100;
  background-color: ${colors.white};
  display: flex;
  flex-direction: row;
  border-radius: 0px 0px 8px 8px;
  padding: 16px 24px;
  border-top: 1px solid ${colors.greyBorder};
  gap: 8px;
  width: 100%;
  align-items: center;
  height: fit-content;
  margin-top: auto;
  justify-content: end;
`;

export const formStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  overflow-y: auto;
`;

export const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding-top: 24px;
  padding-bottom: 24px;
  width: 600px;
`;

export const bodySectionStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

export const bodyHeaderStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const webAddressRowStyle = css`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: baseline;
`;

type Props = {
  businessLine?: AdyenBusinessLine;
};

export const MutateBusinessLineForm = ({ businessLine }: Props) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const schema = yup.object({
    industryCode: yup.string().required("Industry code is required"),
    webAddresses: yup
      .array()
      .of(
        yup.object({
          address: yup.string().required("Web address is required"),
        })
      )
      .min(1, "At least one web address is required"),
  });

  const methods = useForm<AdyenBusinessLineFormType>({
    defaultValues: {
      industryCode: businessLine?.industryCode || "",
      webAddresses:
        businessLine?.webAddresses.map((w) => ({
          address: w,
        })) || [],
    },
    resolver: yupResolver(schema),
  });

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: "webAddresses",
  });

  const { data: industryCodes, isLoading } = useGetBusinessLineIndustryCodes();

  const {
    mutateAsync: createBusinessLine,
  } = useCreateAdyenAccountHolderBusinessLine(id);
  const {
    mutateAsync: updateBusinessLine,
  } = useUpdateAdyenAccountHolderBusinessLine(id, businessLine?.id);

  const onSubmit = async (data: AdyenBusinessLineFormType) => {
    if (!id) {
      return;
    }

    try {
      if (businessLine) {
        await updateBusinessLine({
          data: {
            id: businessLine.id,
            accountHolderId: id,
            industryCode: data.industryCode,
            webAddresses: data.webAddresses.map((w) => w.address),
          },
        });
      } else {
        await createBusinessLine({
          data: {
            accountHolderId: id,
            industryCode: data.industryCode,
            webAddresses: data.webAddresses.map((w) => w.address),
          },
        });
      }
      toastr.success(
        "Success",
        `Adyen account holder ${
          businessLine ? "updated" : "created"
        } successfully`
      );
      navigate("..");
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  if (isLoading) {
    return <SpinnerBlock />;
  }

  return (
    <FormProvider {...methods}>
      <form css={formStyle} onSubmit={methods.handleSubmit(onSubmit)}>
        <div css={bodyStyle}>
          <div css={bodySectionStyle}>
            <FormRow>
              <SelectInput
                name="industryCode"
                label="Industry code"
                placeholder="Select the industry code"
                options={(industryCodes || []).map((code) => ({
                  label: code.name,
                  value: code.code,
                }))}
              />
            </FormRow>
            <StyledText className="bodySubtextSemibold mb-0">
              Web addresses
            </StyledText>
            {fields.map((field, index) => (
              <div key={field.id} css={webAddressRowStyle}>
                <TextInput
                  name={`webAddresses.${index}.address`}
                  placeholder="Enter web address"
                />
                <IconButton
                  icon={<FontAwesomeIcon icon={solid("trash")} />}
                  onClick={() => remove(index)}
                />
              </div>
            ))}
            <ErrorMessage
              name="webAddresses"
              render={({ message }) => (
                <StyledText
                  color={colors.errorDark}
                  className="bodySubtextRegular mb-0"
                >
                  {message}
                </StyledText>
              )}
            />
            <ButtonPrimary onClick={() => append({ address: "" })}>
              Add web address
            </ButtonPrimary>
          </div>
        </div>
        <div css={buttonWrapperStyle}>
          <ButtonInternalLink to={".."}>Cancel</ButtonInternalLink>
          <ButtonAction type="submit" disabled={methods.formState.isSubmitting}>
            {methods.formState.isSubmitting ? (
              <ActionSpinner />
            ) : businessLine ? (
              "Update business line"
            ) : (
              "Create business line"
            )}
          </ButtonAction>
        </div>
      </form>
    </FormProvider>
  );
};
